import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../../core/api.service';
import { CustomerPickupRequest } from '../models/customer-pickup-request';

@Injectable({
  providedIn: 'root'
})
export class CustomerPickupRequestService {
  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService
  ) {}

  searchByLocationAndDateRange(
    locationId: string,
    startDate: Date,
    endDate: Date,
    numberOfRecords: number,
    showVoidedOrders: boolean
  ): Observable<CustomerPickupRequest[]> {
    const params = new HttpParams()
      .set('locationId', locationId)
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString())
      .set('numberOfRecords', String(numberOfRecords))
      .set('showVoidedOrders', String(showVoidedOrders));

    return this.http.get<CustomerPickupRequest[]>(
      this.apiService.buildUrl('api/customerpickuprequest/searchByLocationAndDateRange'),
      { params }
    );
  }

  getFullDetail(cprNumber: string): Observable<CprFullDetail | null> {
    const params = new HttpParams().set('searchValue', cprNumber);
    return this.http
      .get<ApiCprFullDto[]>(this.apiService.buildUrl('api/customerpickuprequest/search'), { params })
      .pipe(map((results) => {
        const match = (results ?? []).find((r) => r.cprNumber === cprNumber);
        if (!match) return null;
        return {
          id: String(match.id ?? ''),
          cprNumber: match.cprNumber ?? '',
          customerReferenceNumber: match.customerReferenceNumber ?? undefined,
          manualBOL: match.manualBOL ?? undefined,
          date: match.date ?? undefined,
          status: match.status ?? 0,
          locationName: match.locationName ?? undefined,
          locationStreet: match.locationStreet ?? undefined,
          locationCity: match.locationCity ?? undefined,
          locationState: match.locationState ?? undefined,
          locationZip: match.locationZip ?? undefined,
          serviceProviderName: match.serviceProviderName ?? undefined,
          shipvia: match.shipvia ?? undefined,
          comments: match.comments ?? undefined,
          callerfirstname: match.callerfirstname ?? undefined,
          callerlastname: match.callerlastname ?? undefined,
          phonenumber: match.phonenumber ?? undefined,
          createdOn: match.createdOn ?? undefined,
          modifiedOn: match.modifiedOn ?? undefined,
          lineItems: (match.details ?? [])
            .filter((d) => (d.quantity ?? 0) > 0)
            .map((d) => ({
              productName: d.productName ?? '',
              quantity: d.quantity ?? 0
            }))
        } satisfies CprFullDetail;
      }));
  }

  update(request: CprUpdateRequest): Observable<unknown> {
    return this.http.put(this.apiService.buildUrl('api/customerpickuprequest'), request);
  }

  voidCpr(cprId: string): Observable<void> {
    return this.http.put<void>(this.apiService.buildUrl(`api/customerpickuprequest/void/${cprId}`), {});
  }

  create(request: CprCreateRequest): Observable<string> {
    return this.http.put<string>(this.apiService.buildUrl('api/customerpickuprequest/createCPRRetrievalRequest'), request);
  }

  search(searchValue: string): Observable<CustomerPickupRequest[]> {
    const params = new HttpParams().set('searchValue', searchValue);
    return this.http.get<ApiCprSearchResult[]>(
      this.apiService.buildUrl('api/customerpickuprequest/search'),
      { params }
    ).pipe(
      map((results) =>
        (results ?? []).map((r) => ({
          id: String(r.id ?? ''),
          cprNumber: r.cprNumber ?? '',
          customerReferenceNumber: r.customerReferenceNumber ?? undefined,
          manualBOL: r.manualBOL ?? undefined,
          date: r.date ?? undefined,
          status: r.status ?? 0,
          locationName: r.locationName ?? undefined,
          serviceProviderName: r.serviceProviderName ?? undefined,
          createdOn: r.createdOn ?? undefined,
          modifiedOn: r.modifiedOn ?? undefined,
          details: r.details ?? undefined
        } satisfies CustomerPickupRequest))
      )
    );
  }
}

export interface CprUpdateRequest {
  id: string;
  customerReferenceNumber?: string;
  requestedDate?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactPhoneNumber?: string;
  specialInstructions?: string;
}

export interface CprCreateRequest {
  locationId: string;
  serviceProviderId: string;
  scheduledPickupDate: string;
  callerfirstname: string;
  callerlastname: string;
  numberofcprtocreate: number;
  phonenumber: string;
  customerReferenceNumber: string;
  comments: string;
  detailList?: CprCreateRequestDetail[];
}

export interface CprCreateRequestDetail {
  retrievalPricingId: string;
  mainProductId: string;
  estimatedQuantity: number;
}

interface ApiCprSearchResult {
  id?: string;
  cprNumber?: string;
  customerReferenceNumber?: string;
  manualBOL?: string;
  date?: string;
  status?: number;
  locationName?: string;
  serviceProviderName?: string;
  createdOn?: string;
  modifiedOn?: string;
  details?: { quantity?: number }[];
}

export interface CprFullDetail {
  id: string;
  cprNumber: string;
  customerReferenceNumber?: string;
  manualBOL?: string;
  date?: string;
  status: number;
  locationName?: string;
  locationStreet?: string;
  locationCity?: string;
  locationState?: string;
  locationZip?: string;
  serviceProviderName?: string;
  shipvia?: string;
  comments?: string;
  callerfirstname?: string;
  callerlastname?: string;
  phonenumber?: string;
  createdOn?: string;
  modifiedOn?: string;
  lineItems: CprLineItem[];
}

export interface CprLineItem {
  productName: string;
  quantity: number;
}

interface ApiCprFullDto {
  id?: string;
  cprNumber?: string;
  customerReferenceNumber?: string;
  manualBOL?: string;
  date?: string;
  status?: number;
  locationName?: string;
  locationStreet?: string;
  locationCity?: string;
  locationState?: string;
  locationZip?: string;
  serviceProviderName?: string;
  shipvia?: string;
  comments?: string;
  callerfirstname?: string;
  callerlastname?: string;
  phonenumber?: string;
  createdOn?: string;
  modifiedOn?: string;
  details?: { productName?: string; quantity?: number }[];
}
